import { Injectable, Signal } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { INgxsFormModel, IUser, UserType, WwwAppState } from 'shared/src/models';
import { IAccountOrder } from './models/account-order';
import { IOrderFormModel } from './pages/orders/models/order-form-model';
import { IAccountOrderDto } from './models/account-order-dto';
import { AccountService } from './account.service';
import { finalize, tap } from 'rxjs';

export class AccountStateAddEvent {
  public static readonly type = '[AccountState] add event';

  constructor(public payload: { event: IAccountOrder }) {}
}

export class AccountStateRemoveEvent {
  public static readonly type = '[AccountState] remove event';

  constructor(public payload: { event: IAccountOrder }) {}
}

export class AccountStateAddEventPlace {
  public static readonly type = '[AccountState] add event place';

  constructor(public payload: { event: IAccountOrder }) {}
}

export class AccountStateRemoveEventPlace {
  public static readonly type = '[AccountState] remove event place';

  constructor(public payload: { event: IAccountOrder }) {}
}

export class AccountStateRecalculateSummary {
  public static readonly type = '[AccountState] recalculate summary';
}

export class AccountStateReset {
  public static readonly type = '[AccountState] reset';
}

export class AccountStateAddOrder {
  public static readonly type = '[AccountState] add order';
}

export interface AccountStateModel {
  loading: boolean;
  events: IAccountOrder[];
  summPrice: string | null;
  form: INgxsFormModel<IOrderFormModel>;
}

@State<AccountStateModel>({
  name: 'account',
  defaults: {
    loading: false,
    events: [],
    summPrice: null,
    form: {
      model: {
        firstname: '',
        lastname: '',
        mobile: '',
        email: '',
        invoiceName: '',
        invoiceNip: '',
        invoiceStreet: '',
        invoiceZipCode: '',
        invoiceCity: '',
        events: [],
        agreementPayment: false,
      },
      errors: {},
      dirty: false,
      disabled: false,
      status: 'pristine',
    },
  },
})
@Injectable()
export class AccountState {
  constructor(
    private readonly _store: Store,
    private readonly _accountService: AccountService,
  ) {}

  @Selector()
  public static loading({ loading }: AccountStateModel): boolean {
    return loading;
  }

  @Selector()
  public static events({ events }: AccountStateModel): IAccountOrder[] {
    return events;
  }

  @Selector()
  public static summPrice({ summPrice }: AccountStateModel): string | null {
    return summPrice;
  }

  @Action(AccountStateAddEvent)
  public addTraining(
    { patchState, getState, dispatch }: StateContext<AccountStateModel>,
    { payload: { event } }: AccountStateAddEvent,
  ): void {
    const { events } = getState();

    if (events.findIndex((e: IAccountOrder) => e.id === event.id) > -1) {
      patchState({
        events: [
          ...events.map((e: IAccountOrder) => {
            return { ...e, places: event.id === e.id ? Number(e.places) + 1 : Number(e.places) };
          }),
        ],
      });
    } else {
      patchState({ events: [{ ...event, places: 1 }, ...events] });
    }

    dispatch([new AccountStateRecalculateSummary()]);
  }

  @Action(AccountStateRemoveEvent)
  public removeEvent(
    { patchState, getState, dispatch }: StateContext<AccountStateModel>,
    { payload: { event } }: AccountStateRemoveEventPlace,
  ) {
    const { events } = getState();
    if (events.length && events.find((e: IAccountOrder) => e.id === event.id)) {
      const indexOfEventToRemove = events.findIndex((e: IAccountOrder) => e.id === event.id);
      const eventsAfterRemove = [...events];
      eventsAfterRemove.splice(indexOfEventToRemove, 1);
      if (indexOfEventToRemove > -1) {
        patchState({
          events: eventsAfterRemove,
        });
      }
    }

    dispatch([new AccountStateRecalculateSummary()]);
  }

  @Action(AccountStateAddEventPlace)
  public addEventPlace(
    { patchState, getState, dispatch }: StateContext<AccountStateModel>,
    { payload: { event } }: AccountStateAddEventPlace,
  ) {
    const { events } = getState();
    patchState({
      events: events.map((e: IAccountOrder) => {
        return {
          ...e,
          places: event.id === e.id ? Number(e.places) + 1 : e.places,
        };
      }),
    });
    dispatch([new AccountStateRecalculateSummary()]);
  }

  @Action(AccountStateRemoveEventPlace)
  public removeEventPlace(
    { patchState, getState, dispatch }: StateContext<AccountStateModel>,
    { payload: { event } }: AccountStateRemoveEventPlace,
  ) {
    const { events } = getState();
    patchState({
      events: events.map((e: IAccountOrder) => {
        return {
          ...e,
          places: event.id === e.id ? Number(e.places) - 1 : e.places,
        };
      }),
    });
    dispatch([new AccountStateRecalculateSummary()]);
  }

  @Action(AccountStateRecalculateSummary)
  public recalculateSummary({ patchState, getState }: StateContext<AccountStateModel>) {
    const { events } = getState();
    const user: Signal<IUser | null> = this._store.selectSignal(WwwAppState.user);
    let summPrice = 0;
    for (const p of events) {
      summPrice = summPrice + Number(p.price) * Number(p.places);
    }
    summPrice = summPrice * (user() ? (user()?.type === UserType.ELITE ? 0.8 : 0.9) : 1);

    patchState({
      summPrice: String(summPrice),
    });
  }

  @Action(AccountStateReset)
  public resetEvents({ patchState }: StateContext<AccountStateModel>) {
    patchState({
      events: [],
    });
  }

  @Action(AccountStateAddOrder)
  public addOrder({ patchState, getState, dispatch }: StateContext<AccountStateModel>) {
    const {
      form: { model },
    } = getState();

    patchState({ loading: true });
    const dto: IAccountOrderDto = {
      agreementPayment: model.agreementPayment ? 1 : 0,
      events: model.events,
    };
    return this._accountService.orderAdd(dto).pipe(
      tap(() => {
        dispatch([new AccountStateReset()]);
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }
}
